import { Button, Modal } from 'antd';
import React, { memo } from 'react';
import styled from 'styled-components';
import translations from 'translations';
import { Language } from 'types/schema';
import utilMessages from 'utils/messages';

const AModal = styled(Modal)`
  .ant-modal-title {
    text-align: center !important;
    font-size: 20px !important;
    font-weight: 500 !important;
    line-height: 26px !important;
    color: #2f382c !important;
  }
  .ant-modal-close {
    top: 16px !important;
  }
  .ant-modal-close-x {
    font-size: 18px !important;
  }
`;

const PolicyModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.32px;
  color: #2f382c;
`;

const PolicyModalTitleHighlight = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #3c9041;
`;

const PolicyModalTitleParagraph = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
`;

const PolicyModalMethodList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  margin: 0;
`;

const PolicyModalMethodListItem = styled.li`
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  color: #596056;
  padding-left: 20px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 10px;
    color: #333;
  }
`;

const PolicyModalDivider = styled.hr`
  border: none;
  border-top: 1px solid #eaebea;
  margin: 24px 0;

  ${({ isNoNeedBorder }) =>
    isNoNeedBorder &&
    `
    border-top: none;
    margin-top: 0;
    `}
`;

const PolicyModalButtonContainer = styled.div`
  margin-top: 24px !important;
  display: flex;
  justify-content: center;
`;
const PolicyModalButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  height: 40px;
  font-weight: 500 !important;
  font-size: 16px !important;
  background: #43a047 !important;
  box-shadow: none !important;
`;

const ClickHereContainer = styled.a`
  display: inline;
  color: #136fd8;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  &:hover {
    color: #136fd8;
  }
`;

interface ProductDetailsPolicyModalProps {
  openPaymentPolicy: boolean;
  setOpenPaymentPolicy: (value: boolean) => void;
  openUomKgNote: boolean;
  setOpenUomKgNote: (value: boolean) => void;
}
const ProductDetailsPolicyModal = (props: ProductDetailsPolicyModalProps) => {
  const { openPaymentPolicy, setOpenPaymentPolicy, openUomKgNote, setOpenUomKgNote } = props;

  const lang = localStorage.getItem('lang') || Language.Vi;

  return (
    <>
      <AModal
        destroyOnHidden={false}
        title={translations(utilMessages.deliveryPolicy)}
        closable={true}
        open={openPaymentPolicy}
        footer={null}
        onCancel={() => {
          setOpenPaymentPolicy(false);
        }}
      >
        <PolicyModalTitle>
          {translations(utilMessages.paymentPolicy1)}
          <br />
          <PolicyModalTitleHighlight>{translations(utilMessages.paymentPolicy1Sub)}</PolicyModalTitleHighlight>
        </PolicyModalTitle>
        <PolicyModalTitleParagraph>{translations(utilMessages.paymentPolicy1SubDesc)}</PolicyModalTitleParagraph>
        <PolicyModalMethodList>
          <PolicyModalMethodListItem>{translations(utilMessages.paymentPolicy1SubDesc1)}</PolicyModalMethodListItem>
          <PolicyModalMethodListItem>{translations(utilMessages.paymentPolicy1SubDesc2)}</PolicyModalMethodListItem>
        </PolicyModalMethodList>
        <PolicyModalDivider />
        <PolicyModalTitle>{translations(utilMessages.paymentPolicy2)}</PolicyModalTitle>
        <PolicyModalTitleParagraph>
          {translations(utilMessages.paymentPolicy2Sub1Desc1)}{' '}
          <span style={{ fontWeight: 500, color: '#2F382C' }}>30,000đ</span>{' '}
          {translations(utilMessages.paymentPolicy2Sub1Desc2)}
        </PolicyModalTitleParagraph>
        <PolicyModalTitleParagraph>
          {translations(utilMessages.paymentPolicy2Sub2Desc1)}{' '}
          <span style={{ fontWeight: 500, color: '#2F382C' }}>400,000đ</span>{' '}
          {translations(utilMessages.paymentPolicy2Sub2Desc2)}{' '}
          <ClickHereContainer
            target="_blank"
            href={
              lang === Language.Vi
                ? 'https://kamereo.vn/blog/wp-content/uploads/2026/05/Chinh-sach-giao-hang-tai-Kamereo-VIE.pdf'
                : 'https://kamereo.vn/blog/wp-content/uploads/2026/05/Delivery-Policy-at-Kamereo-ENG.pdf'
            }
          >
            {translations(utilMessages.here)}.
          </ClickHereContainer>
        </PolicyModalTitleParagraph>
        <PolicyModalButtonContainer>
          <PolicyModalButton
            type="primary"
            onClick={() => {
              setOpenPaymentPolicy(false);
            }}
          >
            {translations(utilMessages.gotIt)}
          </PolicyModalButton>
        </PolicyModalButtonContainer>
      </AModal>
      <AModal
        destroyOnHidden={false}
        title={translations(utilMessages.paymentByUomKg)}
        closable={true}
        open={openUomKgNote}
        footer={null}
        onCancel={() => {
          setOpenUomKgNote(false);
        }}
      >
        <PolicyModalTitle>{translations(utilMessages.paymentByUomTitle1)}</PolicyModalTitle>
        <PolicyModalMethodList>
          <PolicyModalMethodListItem>{translations(utilMessages.paymentByUomTitle1Desc1)}</PolicyModalMethodListItem>
          <PolicyModalMethodListItem>{translations(utilMessages.paymentByUomTitle1Desc2)}</PolicyModalMethodListItem>
        </PolicyModalMethodList>

        <PolicyModalDivider isNoNeedBorder />
        <PolicyModalTitle>{translations(utilMessages.paymentByUomTitle2)}</PolicyModalTitle>
        <PolicyModalMethodList>
          <PolicyModalMethodListItem>
            {translations(utilMessages.paymentByUomTitle2Desc1)}
            <PolicyModalMethodList>
              <PolicyModalMethodListItem>
                {translations(utilMessages.paymentByUomTitle2Desc1SubDesc1)}
              </PolicyModalMethodListItem>

              <PolicyModalMethodListItem>
                {translations(utilMessages.paymentByUomTitle2Desc1SubDesc2)}
              </PolicyModalMethodListItem>
            </PolicyModalMethodList>
          </PolicyModalMethodListItem>

          <PolicyModalMethodListItem>{translations(utilMessages.paymentByUomTitle2Desc2)}</PolicyModalMethodListItem>
        </PolicyModalMethodList>
        <PolicyModalButtonContainer>
          <PolicyModalButton
            type="primary"
            onClick={() => {
              setOpenUomKgNote(false);
            }}
          >
            {translations(utilMessages.gotIt)}
          </PolicyModalButton>
        </PolicyModalButtonContainer>
      </AModal>
    </>
  );
};

export default memo(ProductDetailsPolicyModal);
